import { create } from 'zustand';
import apiService from '../apiService';

// =============================================================================
// TYPES
// =============================================================================

/** A single result from the unified retrieval layer */
export interface UnifiedResultItem {
    content: string;
    document_id: string;
    document_name: string;
    reference: string;
    hierarchy?: string;
    source_service: 'graph' | 'pdf';
    source_tool: string;
    relevance_score: number;
    rrf_score?: number;
    concepts?: string[];
    references?: string[];       // outgoing cross-references
    cited_by?: string[];         // incoming cross-references
    linked_qa?: Array<{ ref: string; target: string; title: string; date: string }>;
    document_type?: string;
    document_full_title?: string;
    article_title?: string;
    capabilities?: string[];
    source_url?: string;
}

/** Response from the unified retrieval layer */
export interface UnifiedToolResponse {
    success: boolean;
    tool: string;
    count: number;
    results: UnifiedResultItem[];
    sources_queried?: string[];
    merge_method?: string;
    partial?: boolean;
    errors?: Array<{ service?: string; error: string; note?: string }>;
}

export interface ToolCallResult {
    id: string;
    tool: string;
    params: Record<string, any>;
    response: UnifiedToolResponse | null;
    timestamp: Date;
    status: 'loading' | 'success' | 'error';
    error?: string;
}

export interface LinkAction {
    label: string;
    targetTool: string;
    params: Record<string, any>;
}

interface QuickSearchState {
    // State
    activeTool: string | null;
    results: ToolCallResult[];
    isLoading: boolean;

    // Actions
    setActiveTool: (tool: string | null) => void;
    addResult: (result: ToolCallResult) => void;
    updateResult: (id: string, updates: Partial<ToolCallResult>) => void;
    clearResults: () => void;
    resetStore: () => void;
    executeToolCall: (tool: string, params: Record<string, any>) => Promise<void>;
}

// =============================================================================
// STORE
// =============================================================================

export const useQuickSearchStore = create<QuickSearchState>((set, get) => ({
    // Initial state
    activeTool: null,
    results: [],
    isLoading: false,

    // Actions
    setActiveTool: (tool) => set({ activeTool: tool }),

    addResult: (result) => set((state) => ({
        results: [result, ...state.results]
    })),

    updateResult: (id, updates) => set((state) => ({
        results: state.results.map(r => r.id === id ? { ...r, ...updates } : r)
    })),

    clearResults: () => set({ results: [] }),

    // BAR-53: full teardown for project-switch / logout — also clears the
    // in-flight tool selection + loading flag so they don't survive into the
    // next project / next user (Quick Research is a session-global singleton).
    resetStore: () => set({ activeTool: null, results: [], isLoading: false }),

    executeToolCall: async (tool, params) => {
        const id = crypto.randomUUID();
        const result: ToolCallResult = {
            id,
            tool,
            params,
            response: null,
            timestamp: new Date(),
            status: 'loading'
        };

        set({ isLoading: true });
        get().addResult(result);

        try {
            const response = await apiService.testTool(tool, params);
            get().updateResult(id, { response, status: 'success' });
        } catch (error) {
            get().updateResult(id, {
                error: error instanceof Error ? error.message : 'Unknown error',
                status: 'error'
            });
        } finally {
            set({ isLoading: false });
        }
    }
}));

// =============================================================================
// TOOL DEFINITIONS — 12 Unified Retrieval Tools
// =============================================================================

export interface ToolField {
    name: string;
    type: 'text' | 'number' | 'checkbox' | 'select';
    required: boolean;
    placeholder?: string;
    defaultValue?: any;
    description?: string;
    options?: Array<{ value: string; label: string }>;
}

export interface ToolDefinition {
    name: string;
    label: string;
    description: string;
    category: 'search' | 'document' | 'intelligence' | 'analysis';
    fields: ToolField[];
}

export const TOOL_DEFINITIONS: ToolDefinition[] = [
    // ── Search Tools ───────────────────────────────────────────────────
    {
        name: 'search_content',
        label: 'Search Content',
        description: 'Semantic search across regulatory text (graph + PDF). Merges results by relevance.',
        category: 'search',
        fields: [
            { name: 'query', type: 'text', required: true, placeholder: 'e.g., own funds requirements', description: 'Natural language search query' },
            { name: 'document_id', type: 'text', required: false, placeholder: 'e.g., 32013R0575 or CRR', description: 'Limit to specific document' },
            { name: 'min_relevance', type: 'number', required: false, defaultValue: 0.6, description: 'Minimum relevance (0-1)' },
            { name: 'max_results', type: 'number', required: false, defaultValue: 10, description: 'Maximum results' },
        ]
    },
    {
        name: 'search_by_title',
        label: 'Search by Title',
        description: 'Find articles/sections by identifier (e.g., "Article 92", "TITLE IV", "§4.3.2").',
        category: 'search',
        fields: [
            { name: 'query', type: 'text', required: true, placeholder: 'e.g., Article 92, TITLE IV', description: 'Article or section identifier' },
            { name: 'document_id', type: 'text', required: false, placeholder: 'e.g., 32013R0575 or CRR', description: 'Limit to specific document' },
            { name: 'max_results', type: 'number', required: false, defaultValue: 10, description: 'Maximum results' },
        ]
    },
    {
        name: 'search_concepts',
        label: 'Search Concepts',
        description: 'Search the regulatory concept ontology (graph only). Finds concept definitions and relationships.',
        category: 'search',
        fields: [
            { name: 'query', type: 'text', required: true, placeholder: 'e.g., own funds, CET1 capital', description: 'Regulatory concept or term' },
            { name: 'document_id', type: 'text', required: false, placeholder: 'e.g., 32013R0575 or CRR', description: 'Limit to specific EU regulation' },
            { name: 'max_results', type: 'number', required: false, defaultValue: 10, description: 'Maximum results' },
        ]
    },
    // ── Document Access Tools ──────────────────────────────────────────
    {
        name: 'get_documents',
        label: 'Available Documents',
        description: 'List all regulatory documents from graph and PDF sources with capabilities metadata.',
        category: 'document',
        fields: [
            { name: 'query', type: 'text', required: false, placeholder: 'e.g., CRR, liquidity', description: 'Keyword filter on titles' },
        ]
    },
    {
        name: 'get_structure',
        label: 'Document Structure',
        description: 'Get hierarchical table of contents for any regulatory document.',
        category: 'document',
        fields: [
            { name: 'document_id', type: 'text', required: true, placeholder: 'e.g., 32013R0575 or CRR', description: 'Document ID (CELEX, common name, or doc_id)' },
        ]
    },
    {
        name: 'get_content',
        label: 'Get Content',
        description: 'Retrieve specific article/section content with intelligence (concepts, cross-references).',
        category: 'document',
        fields: [
            { name: 'document_id', type: 'text', required: true, placeholder: 'e.g., 32013R0575 or CRR', description: 'Document ID' },
            { name: 'path', type: 'text', required: true, placeholder: 'e.g., Article 92, Title II > Chapter 3', description: 'Content path' },
            { name: 'include_intelligence', type: 'checkbox', required: false, defaultValue: true, description: 'Include concepts and cross-references (EU regs only)' },
        ]
    },
    // ── Regulatory Intelligence Tools ──────────────────────────────────
    {
        name: 'get_definition',
        label: 'Get Definition',
        description: 'Get definition of a regulatory concept or term. Graph first, PDF fallback.',
        category: 'intelligence',
        fields: [
            { name: 'term', type: 'text', required: true, placeholder: 'e.g., own funds, CET1 capital', description: 'Regulatory term to define' },
            { name: 'document_id', type: 'text', required: false, placeholder: 'e.g., 32013R0575 (optional)', description: 'Limit to specific document' },
        ]
    },
    {
        name: 'get_concept_map',
        label: 'Concept Map',
        description: 'Map where a concept is used across EU regulations (DEFINES, IMPOSES, SUBJECT_TO, etc.).',
        category: 'intelligence',
        fields: [
            { name: 'concept_name', type: 'text', required: true, placeholder: 'e.g., Own funds', description: 'Concept name to trace' },
            { name: 'document_id', type: 'text', required: false, placeholder: 'e.g., 32013R0575 (optional)', description: 'Filter to one EU regulation' },
            { name: 'max_results', type: 'number', required: false, defaultValue: 50, description: 'Maximum usage entries' },
        ]
    },
    {
        name: 'get_references',
        label: 'Cross-References',
        description: 'Get incoming and outgoing cross-references for an article (EU regulations only).',
        category: 'intelligence',
        fields: [
            { name: 'article_id', type: 'text', required: true, placeholder: 'e.g., Article 92', description: 'Article identifier' },
            { name: 'document_id', type: 'text', required: true, placeholder: 'e.g., 32013R0575 or CRR', description: 'EU regulation document' },
        ]
    },
    {
        name: 'get_calculation',
        label: 'Calculation Logic',
        description: 'Get computational logic (LogicalRules, Parameters, MathematicalFunctions) for an article.',
        category: 'intelligence',
        fields: [
            { name: 'article_id', type: 'text', required: true, placeholder: 'e.g., Article 262', description: 'Article identifier' },
            { name: 'document_id', type: 'text', required: true, placeholder: 'e.g., 32013R0575 or CRR', description: 'EU regulation document' },
        ]
    },
    {
        name: 'trace_dependencies',
        label: 'Trace Dependencies',
        description: 'Follow reference chains across articles. Builds dependency tree (EU regulations only).',
        category: 'intelligence',
        fields: [
            { name: 'article_id', type: 'text', required: true, placeholder: 'e.g., Article 92', description: 'Starting article' },
            { name: 'document_id', type: 'text', required: true, placeholder: 'e.g., 32013R0575 or CRR', description: 'EU regulation document' },
            { name: 'direction', type: 'select', required: false, defaultValue: 'both', description: 'Reference direction', options: [
                { value: 'both', label: 'Both' },
                { value: 'incoming', label: 'Incoming' },
                { value: 'outgoing', label: 'Outgoing' },
            ]},
            { name: 'max_depth', type: 'number', required: false, defaultValue: 2, description: 'Maximum hops (1-4)' },
        ]
    },
    // ── Deep Analysis Tools ────────────────────────────────────────────
    {
        name: 'explore',
        label: 'AI Explore',
        description: 'Ask a complex question about a document using AI analysis (spawns subagent).',
        category: 'analysis',
        fields: [
            { name: 'document_id', type: 'text', required: true, placeholder: 'e.g., doc_id or CRR', description: 'Document to analyze' },
            { name: 'question', type: 'text', required: true, placeholder: 'e.g., How does this relate to...', description: 'Complex question to analyze' },
        ]
    },
];

// =============================================================================
// LINK EXTRACTION — Unified JSON Response
// =============================================================================

/**
 * Extract linkable actions from a unified tool response.
 * Works with structured JSON results (not XML).
 */
export function extractLinksFromResult(tool: string, response: UnifiedToolResponse | null): LinkAction[] {
    const links: LinkAction[] = [];
    if (!response?.results?.length) return links;

    const seen = new Set<string>();

    for (const result of response.results) {
        // Link to get_content for any result with document_id + reference
        if (result.document_id && result.reference && result.reference !== 'Document Catalog' && result.reference !== 'Table of Contents') {
            const key = `content:${result.document_id}:${result.reference}`;
            if (!seen.has(key)) {
                seen.add(key);
                links.push({
                    label: `View ${result.reference}`,
                    targetTool: 'get_content',
                    params: { document_id: result.document_id, path: result.reference }
                });
            }
        }

        // Link to get_structure for document catalog results
        if (result.document_id && (tool === 'get_documents' || result.reference === 'Document Catalog')) {
            const key = `structure:${result.document_id}`;
            if (!seen.has(key)) {
                seen.add(key);
                links.push({
                    label: `TOC: ${result.document_name || result.document_id}`,
                    targetTool: 'get_structure',
                    params: { document_id: result.document_id }
                });
            }
        }

        // Link to get_concept_map for concept results
        if (result.concepts) {
            for (const concept of result.concepts) {
                const key = `concept:${concept}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    links.push({
                        label: `Concept: ${concept}`,
                        targetTool: 'get_concept_map',
                        params: { concept_name: concept }
                    });
                }
            }
        }

        // Link to get_content for cross-referenced articles
        if (result.references && result.document_id) {
            for (const ref of result.references.slice(0, 5)) {
                const key = `ref:${result.document_id}:${ref}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    links.push({
                        label: `Ref: ${ref}`,
                        targetTool: 'get_content',
                        params: { document_id: result.document_id, path: ref }
                    });
                }
            }
        }
    }

    return links;
}
